import { useMemo } from 'react';

import { Spin } from 'antd';
import classNames from 'classnames';

import IntlMessages from '@src/util/IntlMessages';

import { EXPRESSION_VALUE_TYPE } from '../../../constants';
import styles from './counterRenderer.module.less';

const GROUP_SEPARATOR = ',';

const parseStatisticValue = value => {
  if (value == null || value === '') {
    return { negative: '', int: '0', decimal: null };
  }

  const val = String(value);
  const cells = val.match(/^(-?)(\d*)(\.(\d+))?$/);

  if (!cells || val === '-') {
    return { negative: '', int: val, decimal: null };
  }

  return {
    negative: cells[1],
    int: cells[2] || '0',
    decimal: cells[4] ?? null,
  };
};

const formatStatisticValue = (value, groupSeparator = GROUP_SEPARATOR) => {
  const { negative, int, decimal } = parseStatisticValue(value);
  const formattedInt = int.replace(/\B(?=(\d{3})+(?!\d))/g, groupSeparator);

  if (decimal == null) {
    return `${negative}${formattedInt}`;
  }

  return `${negative}${formattedInt}.${decimal}`;
};

const formatIntegerValue = value => formatStatisticValue(Math.round(Number(value) || 0));

const CounterValue = ({ value, className, decimalClassName }) => {
  const { negative, int, decimal } = parseStatisticValue(value ?? 0);
  const formattedInt = int.replace(/\B(?=(\d{3})+(?!\d))/g, GROUP_SEPARATOR);

  return (
    <span className={className}>
      <span className={styles.valueInt}>
        {negative}
        {formattedInt}
      </span>
      {decimal != null && (
        <span className={decimalClassName || styles.valueDecimal}>.{decimal}</span>
      )}
    </span>
  );
};

const getColorForPercentage = (percentage, percentageConfig) => {
  if (!percentageConfig || typeof percentageConfig !== 'object') {
    return null;
  }

  const rules = Object.entries(percentageConfig)
    .map(([threshold, color]) => ({
      threshold: Number(threshold),
      color: color.startsWith('#') ? color : `#${color}`,
    }))
    .sort((a, b) => b.threshold - a.threshold);

  const matchingRule = rules.find(rule => percentage < rule.threshold);

  return matchingRule ? matchingRule.color : null;
};

const StatBullet = ({ variant = 'primary' }) => (
  <span
    className={classNames(styles.colorBullet, {
      [styles.colorBulletPrimary]: variant === 'primary',
      [styles.colorBulletNeutral]: variant === 'neutral',
    })}
  />
);

const LegendRow = ({ label, value, bulletVariant = 'primary' }) => (
  <div className={styles.legendRow}>
    <span className={styles.legendLabel}>
      <StatBullet variant={bulletVariant} />
      <span>{label}</span>
    </span>
    {value != null && <span className={styles.legendValue}>{value}</span>}
  </div>
);

const CounterShell = ({ metric, legend, style }) => (
  <div className={styles.content} style={style}>
    <div className={styles.metricSection}>{metric}</div>
    <div className={styles.legendSection}>{legend}</div>
  </div>
);

const CounterNumberView = ({ data }) => (
  <CounterShell
    metric={
      <div className={styles.primaryRow}>
        <CounterValue value={data?.finished} className={styles.primaryValue} />
        <span className={styles.separator}>/</span>
        <CounterValue value={data?.expected} className={styles.secondaryValue} />
      </div>
    }
    legend={
      <LegendRow
        label={<IntlMessages id="dashboards.chart.form.counter.compliance.rate" />}
        value={`${formatIntegerValue(data?.percentageTotal)}%`}
      />
    }
  />
);

const CounterPercentageView = ({ data, totalColor }) => (
  <CounterShell
    style={totalColor ? { '--statistic-color': totalColor } : undefined}
    metric={
      <div className={styles.primaryRow}>
        <span className={styles.primaryValue}>{formatIntegerValue(data?.percentageTotal)}</span>
        <span className={styles.valueSuffix}>%</span>
      </div>
    }
    legend={
      <>
        <LegendRow
          label={<IntlMessages id="general.completedPlural" />}
          value={formatIntegerValue(data?.completed)}
        />
        <LegendRow
          label={<IntlMessages id="general.notCompletedPlural" />}
          value={formatIntegerValue(data?.notCompleted)}
          bulletVariant="neutral"
        />
      </>
    }
  />
);

const CounterContent = ({ data, loading, widget, ref }) => {
  const valueType = widget?.queryParams?.valueType;
  const percentageConfig = widget?.queryParams?.percentageConfig;

  const totalColor = useMemo(() => {
    if (valueType === EXPRESSION_VALUE_TYPE.NUMBER) {
      return null;
    }
    const percentage = Math.round(Number(data?.percentageTotal) || 0);
    return getColorForPercentage(percentage, percentageConfig) || undefined;
  }, [valueType, data?.percentageTotal, percentageConfig]);

  return (
    <Spin spinning={loading} size="large">
      <div ref={ref} className={styles.container}>
        {valueType === EXPRESSION_VALUE_TYPE.NUMBER ? (
          <CounterNumberView data={data} />
        ) : (
          <CounterPercentageView data={data} totalColor={totalColor} />
        )}
      </div>
    </Spin>
  );
};

export const renderCounterContent = props => <CounterContent {...props} />;
